import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  getDocs,
  collection,
  query,
  orderBy,
} from "firebase/firestore";

import { db } from "../../../firebase/firebase";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

function BellCardIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ac-noti-card-svg" aria-hidden="true">
      <path
        d="M12 4.5a4 4 0 0 1 4 4v2.2c0 1 .3 2 .9 2.8l1 1.4c.5.8 0 1.9-1 1.9H7.1c-1 0-1.5-1.1-1-1.9l1-1.4c.6-.8.9-1.8.9-2.8V8.5a4 4 0 0 1 4-4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 18.5a2.2 2.2 0 0 0 4 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SummaryCard({ label, value, hint, tone = "blue" }) {
  return (
    <div className={`ac-soft-panel ac-noti-summarycard ac-noti-tone-${tone}`}>
      <div className="ac-noti-summary-label">{label}</div>
      <div className="ac-noti-summary-value">{value}</div>
      <div className="ac-noti-summary-hint">{hint}</div>
    </div>
  );
}

function PriorityPill({ value }) {
  const map = {
    Kritisch: "ac-noti-prioritypill-orange",
    Prüfen: "ac-noti-prioritypill-blue",
    Hinweis: "ac-noti-prioritypill-green",
    Info: "ac-noti-prioritypill-neutral",
  };

  return (
    <span className={`ac-noti-prioritypill ${map[value] || "ac-noti-prioritypill-neutral"}`}>
      {value}
    </span>
  );
}

function parseDate(value) {
  if (!value) return null;

  if (value?.toDate && typeof value.toDate === "function") {
    return value.toDate();
  }

  if (value instanceof Date) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function relativeTime(value) {
  const date = parseDate(value);
  if (!date) return "unbekannt";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Minute${minutes === 1 ? "" : "n"}`;
  if (hours < 24) return `vor ${hours} Stunde${hours === 1 ? "" : "n"}`;
  if (days === 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;

  return date.toLocaleDateString("de-AT");
}

function mapAccessRequest(doc) {
  const data = doc.data();

  return {
    id: `access-${doc.id}`,
    sourceId: doc.id,
    type: "Zugangsanfragen",
    priority: "Prüfen",
    title: "Neue Zugangsanfrage",
    description:
      data.message ||
      `${data.name || "Unbekannt"} möchte Zugang für ${Array.isArray(data.requestedClasses) ? data.requestedClasses.join(", ") : "eine Klasse"}.`,
    time: relativeTime(data.createdAt),
    actionLabel: "Öffnen",
    actionPath: "/access-requests",
    unread: String(data.status || "").toLowerCase() === "pending",
    sortDate: parseDate(data.createdAt),
  };
}

function mapFeedback(doc) {
  const data = doc.data();
  const rating = Number(data.rating || 0);

  return {
    id: `feedback-${doc.id}`,
    sourceId: doc.id,
    type: "Feedback",
    priority: rating <= 2 ? "Kritisch" : "Hinweis",
    title: "Neues Feedback",
    description: data.message || "Neue Rückmeldung eingegangen.",
    time: relativeTime(data.createdAt),
    actionLabel: "Öffnen",
    actionPath: "/feedback",
    unread: String(data.status || "").toLowerCase() === "neu",
    sortDate: parseDate(data.createdAt),
  };
}

function mapContact(doc) {
  const data = doc.data();

  return {
    id: `contact-${doc.id}`,
    sourceId: doc.id,
    type: "Inhalte",
    priority: "Info",
    title: "Neue Kontaktanfrage",
    description:
      data.subject ||
      data.message ||
      "Neue Anfrage über das Kontaktformular.",
    time: relativeTime(data.createdAt),
    actionLabel: "Öffnen",
    actionPath: "/notifications",
    unread: String(data.status || "").toLowerCase() === "neu",
    sortDate: parseDate(data.createdAt),
  };
}

export default function NotificationsPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("Alle");

  useEffect(() => {
    let mounted = true;

    async function loadNotifications() {
      try {
        const [requestsSnap, feedbackSnap, contactsSnap] = await Promise.all([
          getDocs(query(collection(db, "accessRequests"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "websiteFeedback"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "websiteContacts"), orderBy("createdAt", "desc"))),
        ]);

        const merged = [
          ...requestsSnap.docs.map(mapAccessRequest),
          ...feedbackSnap.docs.map(mapFeedback),
          ...contactsSnap.docs.map(mapContact),
        ].sort((a, b) => {
          const da = a.sortDate?.getTime?.() || 0;
          const db = b.sortDate?.getTime?.() || 0;
          return db - da;
        });

        if (!mounted) return;
        setItems(merged);
      } catch (error) {
        console.error("Benachrichtigungen konnten nicht geladen werden:", error);
        if (!mounted) return;
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadNotifications();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchesSearch =
        !q ||
        `${item.title} ${item.description} ${item.type} ${item.priority}`
          .toLowerCase()
          .includes(q);

      const matchesFilter =
        filter === "Alle"
          ? true
          : filter === "Ungelesen"
          ? item.unread
          : filter === "Kritisch"
          ? item.priority === "Kritisch"
          : item.type === filter;

      return matchesSearch && matchesFilter;
    });
  }, [items, search, filter]);

  const stats = useMemo(() => {
    const unread = items.filter((item) => item.unread).length;
    const critical = items.filter((item) => item.priority === "Kritisch").length;
    const access = items.filter((item) => item.type === "Zugangsanfragen").length;
    const feedback = items.filter((item) => item.type === "Feedback").length;

    return { unread, critical, access, feedback };
  }, [items]);

  const openItem = (item) => {
    setItems((prev) =>
      prev.map((entry) =>
        entry.id === item.id ? { ...entry, unread: false } : entry
      )
    );

    navigate(item.actionPath);
  };

  const markAllRead = () => {
    setItems((prev) => prev.map((item) => ({ ...item, unread: false })));
  };

  return (
    <DashboardLayout title="Benachrichtigungen">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-noti-hero ac-noti-hero-compact">
          <div className="ac-noti-hero-grid">
            <div>
              <h2 className="ac-noti-hero-title">Benachrichtigungsübersicht</h2>
              <p className="ac-noti-hero-text">
                Hier siehst du neue Hinweise, kritische Meldungen, Zugangsanfragen
                und offene Rückmeldungen im Admin-Panel. So erkennst du schneller,
                wo gerade wirklich Handlungsbedarf besteht.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard
                  label="Neu"
                  value={loading ? "…" : stats.unread}
                  hint="ungelesene Meldungen"
                  tone="orange"
                />
                <SummaryCard
                  label="Kritisch"
                  value={loading ? "…" : stats.critical}
                  hint="mit direktem Prüfbedarf"
                  tone="orange"
                />
                <SummaryCard
                  label="Feedback"
                  value={loading ? "…" : stats.feedback}
                  hint="feedback-bezogene Hinweise"
                  tone="blue"
                />
                <SummaryCard
                  label="Zugang"
                  value={loading ? "…" : stats.access}
                  hint="neue Zugangsanfragen"
                  tone="green"
                />
              </div>
            </div>

            <div className="ac-noti-hero-side">
              <div className="ac-noti-acebox ac-noti-acebox-compact">
                <AceAvatar variant="ruhig1" size={56} />
                <div className="ac-noti-acebubble ac-noti-acebubble-compact">
                  Ace sagt: Kritische Meldungen und neue Zugangsanfragen springen dir
                  hier sofort ins Auge.
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <button
                  type="button"
                  onClick={markAllRead}
                  className="ac-ghostbtn ac-noti-rulesbtn"
                >
                  Alle als gelesen markieren
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/rules")}
                  className="ac-primarybtn ac-noti-rulesbtn"
                >
                  Zu den Regeln
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel p-4 sm:p-5">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_220px]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche nach Titel, Beschreibung oder Bereich…"
              className="ac-feedback-input"
            />

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ac-select w-full"
            >
              <option>Alle</option>
              <option>Ungelesen</option>
              <option>Kritisch</option>
              <option>Feedback</option>
              <option>Inhalte</option>
              <option>Zugangsanfragen</option>
            </select>
          </div>
        </section>

        <section className="grid gap-3">
          {loading ? (
            <div className="ac-soft-panel ac-noti-empty">
              <h3 className="ac-noti-empty-title">Lade Benachrichtigungen…</h3>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="ac-soft-panel ac-noti-empty">
              <h3 className="ac-noti-empty-title">Keine Treffer</h3>
              <p className="ac-noti-empty-text">
                Für deine aktuelle Suche oder den gewählten Filter sind gerade keine
                Benachrichtigungen vorhanden.
              </p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openItem(item)}
                className={[
                  "ac-soft-panel",
                  "ac-noti-card",
                  "ac-noti-card-clickable",
                  item.priority === "Kritisch"
                    ? "is-critical"
                    : item.unread
                    ? "is-unread"
                    : "is-neutral",
                ].join(" ")}
              >
                <div className="ac-noti-card-inner">
                  <div className="ac-noti-card-left">
                    <div className="ac-noti-card-icon">
                      <BellCardIcon />
                    </div>

                    <div className="min-w-0">
                      <div className="ac-noti-card-head">
                        <h3 className="ac-noti-card-title">{item.title}</h3>

                        <span
                          className={`ac-noti-typebadge ${
                            item.type === "Zugangsanfragen"
                              ? "ac-noti-typebadge-green"
                              : item.type === "Feedback"
                              ? "ac-noti-prioritypill-blue"
                              : item.type === "Inhalte"
                              ? "ac-noti-prioritypill-green"
                              : "ac-noti-prioritypill-neutral"
                          }`}
                        >
                          {item.type}
                        </span>

                        <PriorityPill value={item.priority} />

                        {item.unread && (
                          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-orange-400" />
                        )}
                      </div>

                      <p className="ac-noti-card-text">{item.description}</p>
                      <div className="ac-noti-card-time">{item.time}</div>
                    </div>
                  </div>

                  <div className="ac-noti-card-right">
                    <span className="ac-primarybtn ac-noti-actionbtn">
                      {item.actionLabel}
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}